/**
 * O recorte manual na aba Documentos (E214.F1, GUS-314): `POST /casos/{caseId}/recortes`.
 *
 * O perito escolhe a página de um documento do caso, desenha a área e diz o que ela é. A área vai em
 * frações da página (a mesma convenção do canvas da grafotécnica, `bbox_coords`), e o servidor
 * recorta a página dele: o original não muda, o recorte é um derivado com hash próprio.
 */
import axios, { type AxiosInstance } from 'axios';

import type { NormalizedBoundingBox } from '@/lib/bbox_coords';

export type DestinoDoRecorte =
  | 'identidade_frente'
  | 'identidade_verso'
  | 'selfie'
  | 'comprovante_ted';

/** A ordem em que os destinos aparecem na tela. */
export const DESTINOS_DO_RECORTE: readonly DestinoDoRecorte[] = [
  'identidade_frente',
  'identidade_verso',
  'selfie',
  'comprovante_ted',
];

export interface PedidoDeRecorte {
  documento_id: string;
  pagina: number;
  area: NormalizedBoundingBox;
  destino: DestinoDoRecorte;
}

export interface RecorteFeitoData {
  destino: DestinoDoRecorte;
  documento_id: string;
  pagina: number;
  area: NormalizedBoundingBox;
  sha256: string;
  foto_id: string | null;
  /** No comprovante: quantos créditos foram lidos; zero é o recebido e não lido. */
  creditos: number | null;
}

/** Por que o recorte não foi gravado, na língua da tela. */
export type MotivoDaRecusaDoRecorte =
  | 'concluido'
  | 'pagina'
  | 'formato'
  | 'original'
  | 'nao_encontrado'
  | 'falha';

const POR_DETALHE: Readonly<Record<string, MotivoDaRecusaDoRecorte>> = {
  EXAME_ELETRONICO_CONCLUIDO: 'concluido',
  PAGE_NOT_FOUND: 'pagina',
  RECORTE_FORMATO_NAO_SUPORTADO: 'formato',
  RECORTE_NAO_RENDERIZOU: 'formato',
  RECORTE_IMAGEM_INVALIDA: 'formato',
  ORIGINAL_DIVERGENTE: 'original',
  DOCUMENT_NOT_FOUND: 'nao_encontrado',
  CASE_NOT_FOUND: 'nao_encontrado',
};

export class RecorteDoDocumentoError extends Error {
  constructor(readonly motivo: MotivoDaRecusaDoRecorte) {
    super(motivo);
    this.name = 'RecorteDoDocumentoError';
  }
}

function motivoDoErro(err: unknown): MotivoDaRecusaDoRecorte {
  if (!axios.isAxiosError(err)) return 'falha';
  const detalhe: unknown = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  return typeof detalhe === 'string' ? (POR_DETALHE[detalhe] ?? 'falha') : 'falha';
}

export async function recortarDocumento(
  client: AxiosInstance,
  caseId: string,
  pedido: PedidoDeRecorte,
): Promise<RecorteFeitoData> {
  try {
    const { data } = await client.post<RecorteFeitoData>(`/casos/${caseId}/recortes`, pedido);
    return data;
  } catch (err) {
    throw new RecorteDoDocumentoError(motivoDoErro(err));
  }
}

/** A frase do resultado: o que aconteceu com o recorte em cada destino. */
export type ResultadoDoRecorte = 'foto' | 'verso' | 'credito_lido' | 'credito_nao_lido';

export function resultadoDoRecorte(feito: RecorteFeitoData): ResultadoDoRecorte {
  if (feito.destino === 'comprovante_ted') {
    return (feito.creditos ?? 0) > 0 ? 'credito_lido' : 'credito_nao_lido';
  }
  return feito.destino === 'identidade_verso' ? 'verso' : 'foto';
}
